import { useState, useCallback, useRef, useEffect } from 'react';
import { startChatStream } from '@/services/stream';
import { conversationsService } from '@/services/conversations';
import type { ChatMessage } from '@/types';
import type { ComposerSendOptions } from '@/types/composer';
import type { ChatStreamHandle } from '@/services/stream';
import {
  consumeConversationAttachmentMeta,
  consumeConversationAttachmentText,
  storeConversationAttachment,
  toMessageAttachment,
} from '@/utils/conversationAttachment';

export interface UseChatOptions {
  /** Persist user + assistant rounds to SQLite via REST (requires `conversationId`) */
  persistMessages?: boolean;
  /** Runs after persistence + reload (e.g. refresh conversation list sidebar) */
  onPersistComplete?: () => void | Promise<void>;
  /** Skip loading messages from API (guided template flows) */
  skipHydration?: boolean;
}

export function useChat(conversationId: string | undefined, options?: UseChatOptions) {
  const persistMessages = options?.persistMessages !== false && Boolean(conversationId);
  const onPersistComplete = options?.onPersistComplete;
  const skipHydration = options?.skipHydration === true;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const streamRef = useRef<ChatStreamHandle | null>(null);
  const hydrationGen = useRef(0);

  const reloadMessagesFromDb = useCallback(async () => {
    streamRef.current?.abort();
    streamRef.current = null;
    setIsStreaming(false);
    if (!conversationId) return;
    try {
      const next = await conversationsService.getMessages(conversationId);
      setMessages(next);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e : new Error(String(e)));
      setMessages([]);
    }
  }, [conversationId]);

  useEffect(() => {
    const gen = ++hydrationGen.current;
    streamRef.current?.abort();
    streamRef.current = null;
    setIsStreaming(false);

    if (!conversationId) {
      setMessages([]);
      setError(null);
      setIsLoadingMessages(false);
      return undefined;
    }

    if (skipHydration) {
      setMessages([]);
      setError(null);
      setIsLoadingMessages(false);
      return undefined;
    }

    void (async () => {
      setIsLoadingMessages(true);
      try {
        const next = await conversationsService.getMessages(conversationId);
        if (gen !== hydrationGen.current) return;
        if (streamRef.current !== null) return;
        setMessages(next);
        setError(null);
      } catch (e: unknown) {
        if (gen !== hydrationGen.current) return;
        setMessages([]);
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (gen === hydrationGen.current) setIsLoadingMessages(false);
      }
    })();

    return undefined;
  }, [conversationId, skipHydration]);

  const sendMessage = useCallback(
    (rawText: string, options?: ComposerSendOptions) => {
      const messageText = rawText.trim();
      if (!messageText || !conversationId) return;

      void (async () => {
        let attachmentMeta = null as ReturnType<typeof consumeConversationAttachmentMeta>;
        let attachmentContent = null as ReturnType<typeof consumeConversationAttachmentText>;

        if (options?.attachment) {
          await storeConversationAttachment(conversationId, options.attachment);
        }

        attachmentMeta = consumeConversationAttachmentMeta(conversationId);
        attachmentContent = consumeConversationAttachmentText(conversationId);

        const attachment =
          attachmentMeta && attachmentContent
            ? {
                ...toMessageAttachment(attachmentMeta),
                content: attachmentContent,
              }
            : undefined;

        streamRef.current?.abort();
        streamRef.current = null;
        setIsStreaming(true);
        setError(null);

        const userMessage: ChatMessage = {
          id: `local-user-${Date.now()}`,
          conversationId,
          type: 'user',
          content: messageText,
          createdAt: new Date().toISOString(),
        };

        const assistantMessageId = `local-assistant-${Date.now() + 1}`;
        const assistantPlaceholder: ChatMessage = {
          id: assistantMessageId,
          conversationId,
          type: 'assistant',
          content: '',
          createdAt: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, userMessage, assistantPlaceholder]);

        let assistantAccumulator = '';

        streamRef.current = startChatStream({
          message: messageText,
          conversationId,
          attachment,
          onChunk: (chunk) => {
            assistantAccumulator += chunk;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId ? { ...m, content: m.content + chunk } : m
              )
            );
          },
          onComplete: () => {
            streamRef.current = null;
            setIsStreaming(false);
            void (async () => {
              if (!(persistMessages && conversationId)) {
                await onPersistComplete?.();
                return;
              }

              try {
                const userPayload: { type: 'user'; content: string; attachment?: unknown } = {
                  type: 'user',
                  content: messageText,
                };
                if (attachment) {
                  userPayload.attachment = {
                    kind: attachment.kind,
                    fileName: attachment.fileName,
                  };
                }
                await conversationsService.addMessage(conversationId, userPayload);
                await conversationsService.addMessage(conversationId, {
                  type: 'assistant',
                  content: assistantAccumulator,
                });
                await reloadMessagesFromDb();
                await onPersistComplete?.();
              } catch (e: unknown) {
                const err = e instanceof Error ? e : new Error(String(e));
                setError(err);
              }
            })();
          },
          onError: (err) => {
            streamRef.current = null;
            setError(err);
            setIsStreaming(false);
            setMessages((prev) => prev.filter((m) => m.id !== assistantMessageId));
          },
        });
      })();
    },
    [conversationId, persistMessages, onPersistComplete, reloadMessagesFromDb]
  );

  const cancelStream = useCallback(() => {
    streamRef.current?.abort();
    streamRef.current = null;
    setIsStreaming(false);
  }, []);

  return {
    messages,
    isStreaming,
    isLoadingMessages,
    error,
    sendMessage,
    cancelStream,
    reloadMessages: reloadMessagesFromDb,
  };
}
